/**
 * Histogramme ordinal — durée de vie ou jours d'activité.
 *
 * Les seaux sont ORDONNÉS : « 3 – 6 jours » vient forcément après « 2 jours ».
 * D'où la rampe séquentielle d'une seule teinte, du plus sombre au plus clair,
 * plutôt qu'une couleur unique : le lecteur voit l'ordre dans la couleur en
 * plus de la position.
 *
 * Seaux nommés et non largeur fixe : la distribution a un pic énorme sur un
 * seul jour et une queue de plus d'un an. Des tranches d'égale largeur
 * donneraient une barre géante et vingt barres vides.
 */

import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS, SEQUENTIAL_RAMP } from '../lib/theme';
import type { DataColumn, LabelledBucket } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

interface Row extends LabelledBucket {
  share: number;
}

const columns: ReadonlyArray<DataColumn<Row>> = [
  { key: 'label', header: 'Tranche', render: (row) => row.label },
  { key: 'users', header: 'Comptes', numeric: true, render: (row) => formatInteger(row.users) },
  { key: 'share', header: 'Part', numeric: true, render: (row) => formatPercent(row.share) },
];

interface Props {
  data: readonly LabelledBucket[];
  title: string;
  subtitle: string;
}

export function EngagementHistogram({ data, title, subtitle }: Props) {
  const total = data.reduce((sum, bucket) => sum + bucket.users, 0);
  const rows: Row[] = data.map((bucket) => ({
    ...bucket,
    share: total > 0 ? (bucket.users / total) * 100 : 0,
  }));

  return (
    <ChartCard title={title} subtitle={subtitle} rows={rows} columns={columns} chartHeight={280}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 22, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: 11 }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            interval={0}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatInteger} />}
          />
          <Bar
            isAnimationActive={false}
            dataKey="users"
            name="Comptes"
            radius={[4, 4, 0, 0]}
            maxBarSize={BAR_MAX_SIZE * 2}
          >
            {/* Un pas de la rampe par seau, dans l'ordre : la couleur redouble
                la position pour dire l'ordre. */}
            {rows.map((row, index) => (
              <Cell
                key={row.label}
                fill={SEQUENTIAL_RAMP[Math.min(index, SEQUENTIAL_RAMP.length - 1)]}
              />
            ))}
            <LabelList
              dataKey="share"
              position="top"
              fill={COLORS.textSecondary}
              fontSize={11}
              formatter={(value: unknown) => formatPercent(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
